import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { usePraxis } from "../context/PraxisContext";
import { getIconComponent } from "../data/icons";

// Fallback-Werte, falls im Admin nichts hinterlegt ist.
const FALLBACK_TERMIN_BETREFF = "Terminvereinbarung Videosprechstunde Menopause";
const FALLBACK_TERMIN_BODY = `Sehr geehrte Frau Dr. Scheer,

ich möchte gerne Ihre Videosprechstunde in Anspruch nehmen und einen Termin mit Ihnen vereinbaren.

Ich freue mich auf ein persönliches und vertrauliches Gespräch und darauf, meine Fragen und Anliegen mit Ihnen besprechen zu können.

Bitte teilen Sie mir mit, welche Termine möglich sind und ob Sie vorab noch weitere Informationen von mir benötigen.

Meine Kontaktdaten:

Name:
Telefonnummer:
eMail:

Vielen Dank für Ihre Rückmeldung.

Mit freundlichen Grüßen
`;

export default function Wechseljahre() {
  const { settings: PRAXIS, menopause: MP, mail } = usePraxis();

  // Betreff + Body kommen aus dem Admin-Bereich (mit Fallback).
  const subject = (mail?.subject_template && mail.subject_template.trim())
    ? mail.subject_template : FALLBACK_TERMIN_BETREFF;
  const body = (mail?.autoresponder && mail.autoresponder.trim())
    ? mail.autoresponder : FALLBACK_TERMIN_BODY;
  const mailto = `mailto:${PRAXIS.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  // Rückwärtskompatibilität: Wenn `paragraphs` leer ist, altes `understand_prose` (HTML) rendern
  const hasStructuredParagraphs = Array.isArray(MP.paragraphs) && MP.paragraphs.length > 0;

  return (
    <>
      <section className="section wj-hero" data-testid="wj-hero-section">
        <div className="section-inner" style={{ maxWidth: 820, margin: "0 auto" }}>
          <Link to="/" className="btn btn-outline" style={{ marginBottom: "1.5rem" }} data-testid="wj-back-btn">
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>
          {MP.hero_label && <p className="section-label">{MP.hero_label}</p>}
          <h1>
            {MP.hero_title}
            {MP.hero_em && <><br /><em>{MP.hero_em}</em></>}
          </h1>
          {MP.hero_lead && <p className="wj-lead" style={{ whiteSpace: "pre-line" }}>{MP.hero_lead}</p>}
          <a href={mailto} className="btn btn-gold" data-testid="wj-termin-btn">
            <Mail size={16} /> Termin vereinbaren
          </a>
        </div>
      </section>

      {/* MENOPAUSE VERSTEHEN */}
      {(MP.understand_title || hasStructuredParagraphs || MP.understand_prose) && (
        <section className="wj-section wj-understand" data-testid="wj-understand-section">
          <div className="section-inner" style={{ maxWidth: 820, margin: "0 auto" }}>
            <div className="section-head">
              {MP.understand_label && <p className="section-label">{MP.understand_label}</p>}
              {MP.understand_title && (
                <h2 className="section-title">
                  {MP.understand_title}
                  {MP.understand_subtitle && <><br />{MP.understand_subtitle}</>}
                </h2>
              )}
            </div>
            <div className="wj-prose">
              {hasStructuredParagraphs
                ? MP.paragraphs.map((p, i) => (
                    p.type === "quote"
                      ? <blockquote key={i}>{p.text}</blockquote>
                      : <p key={i} style={{ whiteSpace: "pre-line" }}>{p.text}</p>
                  ))
                : (MP.understand_prose && (
                    // Fallback: altes HTML rendern (nur wenn keine paragraphs vorhanden)
                    <div dangerouslySetInnerHTML={{ __html: sanitizeProse(MP.understand_prose) }} />
                  ))
              }
            </div>
          </div>
        </section>
      )}

      {/* SO FUNKTIONIERT ES */}
      {MP.steps && MP.steps.length > 0 && (
        <section className="wj-section wj-ablauf" data-testid="wj-ablauf-section">
          <div className="section-inner" style={{ maxWidth: 1080, margin: "0 auto" }}>
            <div className="section-head">
              {MP.ablauf_label && <p className="section-label">{MP.ablauf_label}</p>}
              {MP.ablauf_title && (
                <h2 className="section-title">{MP.ablauf_title}</h2>
              )}
              {MP.ablauf_lead && <p className="lead">{MP.ablauf_lead}</p>}
            </div>

            <ol className="wj-steps" data-testid="wj-steps">
              {MP.steps.map((s, i) => {
                const Icon = getIconComponent(s.icon);
                return (
                  <li className="wj-step" key={i} data-testid={`wj-step-${i + 1}`}>
                    <div className="wj-step-num">{i + 1}</div>
                    <div className="wj-step-icon">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div className="wj-step-body">
                      <h3>{s.title}</h3>
                      <p>{s.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {MP.fineprint && (
              <div className="wj-fineprint" data-testid="wj-fineprint" style={{ whiteSpace: "pre-line" }}>
                {MP.fineprint}
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: "3rem" }}>
              <a href={mailto} className="btn btn-gold" data-testid="wj-bottom-cta">
                <Mail size={16} /> Termin vereinbaren
              </a>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

/**
 * Sanitisiert nur die erlaubten Tags im Fallback-Prose-HTML (falls Alt-Content noch nicht migriert).
 */
function sanitizeProse(html) {
  if (!html) return "";
  const allowed = ["p", "br", "em", "strong", "blockquote"];
  let s = html.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[c]);
  allowed.forEach((tag) => {
    const re1 = new RegExp(`&lt;${tag}&gt;`, "g");
    const re2 = new RegExp(`&lt;/${tag}&gt;`, "g");
    s = s.replace(re1, `<${tag}>`).replace(re2, `</${tag}>`);
  });
  return s;
}
